/* DeleteBuildModal — admin-only delete-build confirmation (type-to-confirm).
   Viper Goose-7 handoff, adapted for the live app: AWAIT the API before navigating
   (onDeleted does GooseAPI.deleteBuild → nav), keep the busy spinner up in flight,
   and on error keep the modal OPEN + show the message so a failed DELETE never
   silently drops the row while the server still has the build.

   Loaded as its own screen file (mirrors reportissue.jsx): main.jsx imports it, it
   assigns window.DeleteBuildModal, and jobdetail.jsx mounts <DeleteBuildModal/>.
   Reuses the shared modal classes (fb-overlay/fb-modal/fb-head/…/btn-danger) + the
   .del-callout / .input-bad / .btn-danger-ghost additions in app.css. */
function DeleteBuildModal({ job, onClose, onDeleted }) {
  const [confirm, setConfirm] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const match = confirm.trim() === job.name;

  React.useEffect(() => {
    const k = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  }, [busy]);

  const doDelete = async () => {
    if (!match || busy) return;
    setBusy(true); setErr(null);
    try {
      await onDeleted();   // jobdetail: await GooseAPI.deleteBuild(job.id) THEN navigate away
      // on success the screen navigates; no need to unset busy (modal unmounts)
    } catch (e) {
      // keep the modal open so a failed DELETE doesn't drop the row from the UI
      // while the server still has the build.
      setBusy(false);
      setErr((e && e.data && e.data.error) || (e && e.message) || 'Delete failed — the build was not removed.');
    }
  };
  const close = () => { if (!busy) onClose(); };

  return ReactDOM.createPortal(
    <div className="fb-overlay" onClick={close}>
      <div className="fb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="fb-head">
          <div>
            <div className="row gap-8" style={{ marginBottom: 7 }}>
              <span className="chip" style={{ background: 'var(--fail)', color: '#fff', fontWeight: 700 }}><I.trash size={12} /> Delete</span>
              <span className="chip">Admin</span>
            </div>
            <h2 className="fb-title">Delete this build?</h2>
            <p className="fb-sub">This permanently removes the build, its artifacts, and logs. This cannot be undone.</p>
          </div>
          <button className="icon-act" onClick={close} title="Close" disabled={busy}><I.x size={16} /></button>
        </div>

        <div className="fb-body">
          <div className="del-callout">
            <span className="del-callout-ico"><I.warn size={16} /></span>
            <div>
              <div className="mono" style={{ fontSize: 12.5, fontWeight: 700 }}>{job.name}</div>
              <div className="muted" style={{ fontSize: 11.5, marginTop: 1 }}>{job.id} · {job.tokensK}K tokens · ${Number(job.cost || 0).toFixed(2)} spent</div>
            </div>
          </div>
          <label className="field-lbl" style={{ marginTop: 14 }}>Type the build name to confirm</label>
          <input className={'input' + (confirm && !match ? ' input-bad' : '')} value={confirm} onChange={e => setConfirm(e.target.value)}
            placeholder={job.name} autoFocus spellCheck={false} disabled={busy} style={{ fontFamily: 'var(--mono)', fontSize: 12.5 }} />
          {err && <p style={{ fontSize: 12, color: 'var(--fail)', margin: '10px 2px 0' }} role="alert">{err}</p>}
        </div>

        <div className="fb-foot">
          <span className="muted" style={{ fontSize: 11.5 }}>Irreversible</span>
          <div className="row gap-10">
            <button className="btn btn-quiet" onClick={close} disabled={busy}>Cancel</button>
            <button className="btn btn-danger" onClick={doDelete} disabled={!match || busy}
              title={match ? 'Permanently delete' : 'Type the exact build name first'}>
              {busy ? <React.Fragment><span className="spinner"></span> Deleting…</React.Fragment> : <React.Fragment><I.trash size={15} /> Delete build</React.Fragment>}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

window.DeleteBuildModal = DeleteBuildModal;
